import React, { useRef, useState, useEffect } from 'react';
import { cn } from '../../utils/cn';

interface TextRotateProps {
    texts: string[];
    rotationDuration?: number;
    pauseDuration?: number;
    prefixText?: string;
    className?: string;
    subfixText?: string;
    align?: 'left' | 'center' | 'right';
}

// The static `text-rotate` keyframe only has four dwell stops, so any other
// item count pauses on empty faces. Build an N-stop keyframe that dwells for
// 60% of each segment then turns — the same pause-to-segment ratio the original
// four-step keyframe used, so four items reproduce it exactly.
const buildRotationKeyframes = (count: number): string => {
    const segment = 100 / count;
    const step = 360 / count;
    const stops: string[] = [];
    for (let face = 0; face < count; face++) {
        const start = +(face * segment).toFixed(3);
        const end = +(face * segment + segment * 0.6).toFixed(3);
        stops.push(
            `${start}%, ${end}% { transform: rotateX(${face * step}deg); }`,
        );
    }
    stops.push('100% { transform: rotateX(360deg); }');
    return stops.join(' ');
};
export const TextRotate = React.memo(function TextRotate({
    texts,
    rotationDuration = 8,
    pauseDuration = 0.5,
    prefixText = '',
    subfixText = '',
    className,
    align = 'left',
}: TextRotateProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isInViewport, setIsInViewport] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setIsInViewport(entry?.isIntersecting ?? false),
            { threshold: 0.1 },
        );
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        return () => observer.disconnect();
    }, []);
    const totalDuration = rotationDuration + pauseDuration * texts.length;
    const count = texts.length;
    const angle = 360 / count;
    // The shared `text-rotate` keyframe has four dwell stops, so a three-face
    // layout (the Get Matched selector) pauses on empty faces. Only that case
    // is rerouted to a matching three-stop keyframe + tighter cylinder radius
    // (the regular-triangle apothem); every other count keeps the original
    // 0.55em radius and shared keyframe untouched.
    const isThreeFace = count === 3;
    const radius = isThreeFace
        ? (0.55 / Math.tan(Math.PI / 3)).toFixed(4)
        : '0.55';
    const keyframeName = 'ap-text-rotate-3';
    return (
        <div
            ref={containerRef}
            className={cn('relative mx-auto', className)}
            style={
                {
                    '--rotation-angle': `${angle}deg`,
                    '--rotation-duration': `${totalDuration}s`,
                } as React.CSSProperties
            }
        >
            {isThreeFace && (
                <style>{`@keyframes ${keyframeName} { ${buildRotationKeyframes(count)} }`}</style>
            )}
            <div className="flex flex-col items-start text-left text-[#fafafa] md:flex-row md:items-center">
                {prefixText && (
                    <span className="min-h-[1.16em] leading-[1.16em] md:h-[1em] md:leading-[1em] md:whitespace-pre">
                        {prefixText}
                    </span>
                )}
                <span
                    className={cn(
                        'inline-block',
                        align === 'center' && 'w-full',
                        align === 'right' && 'w-full',
                    )}
                >
                    <div className="relative h-[1.16em] w-auto leading-[1.16em] perspective-[3000px] md:h-[1em] md:leading-[1em]">
                        <div
                            className={cn(
                                'absolute h-full w-full transform-gpu md:top-[-0.08em]',
                                'will-change-transform',
                                'transform-3d',
                                'origin-[50%_50%]',
                                'origin-center',
                                !isThreeFace && [
                                    'direction-[reverse]',
                                    isInViewport
                                        ? 'animate-text-rotate'
                                        : 'animate-none',
                                ],
                            )}
                            style={
                                isThreeFace
                                    ? {
                                          animation: isInViewport
                                              ? `${keyframeName} var(--rotation-duration) ease-in-out infinite reverse`
                                              : 'none',
                                      }
                                    : undefined
                            }
                        >
                            {texts.map((text, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        'absolute w-full text-left whitespace-nowrap will-change-transform [-webkit-backface-visibility:hidden] backface-hidden',
                                        align === 'right' && 'text-right',
                                        align === 'center' && 'text-center',
                                    )}
                                    style={{
                                        transform: `rotateX(${angle * index}deg) translateZ(${radius}em)`,
                                    }}
                                >
                                    {text}
                                </div>
                            ))}
                        </div>
                    </div>
                </span>
                {subfixText && (
                    <span className="md:whitespace-pre">{subfixText}</span>
                )}
            </div>
        </div>
    );
});
